import {
  DeliveryContainerUtilisationPeakBandsDto,
  KnownHistoricalUtilisationPeakBandsMetadataGroupBy,
  KnownPeakBandRunBand
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { ChartData, ScriptableContext } from 'chart.js';
import { naturalSort } from '../../../core/util/natural-sort.util';
import { sortByLockerSize } from '../../lockers/logic/locker-ui.constants';

// The band keys the peak-bands endpoint returns. Derived from the SDK enum so the
// maps below stop compiling if the API ever adds or renames a band, rather than
// silently falling back to the neutral colour.
export type CapacityBand = `${KnownPeakBandRunBand}`;

// Legend order, ascending severity (empty → at-capacity). Listed explicitly rather
// than read off the enum so the left-to-right order doesn't depend on the order
// autorest happens to emit members in.
export const CAPACITY_BANDS: readonly CapacityBand[] = [
  KnownPeakBandRunBand.Empty,
  KnownPeakBandRunBand.Under5,
  KnownPeakBandRunBand.Under20,
  KnownPeakBandRunBand.Under40,
  KnownPeakBandRunBand.From40To60,
  KnownPeakBandRunBand.Over60,
  KnownPeakBandRunBand.Over80,
  KnownPeakBandRunBand.Over95,
  KnownPeakBandRunBand.Full
];

// Green → amber → red ramp, one colour per band.
export const CAPACITY_BAND_COLOURS: Record<CapacityBand, string> = {
  empty: '#44914D',
  'under-5': '#58B763',
  'under-20': '#9DD68D',
  'under-40': '#CCEF90',
  'from-40-to-60': '#F5EF3F',
  'over-60': '#FFD54F',
  'over-80': '#FFA725',
  'over-95': '#EB5C13',
  full: '#B71D1D'
};

// Legend/tooltip text per band, verbatim from the design's legend.
export const CAPACITY_BAND_LABELS: Record<CapacityBand, string> = {
  empty: '0%',
  'under-5': '<5%',
  'under-20': '<20%',
  'under-40': '<40%',
  'from-40-to-60': '40-60',
  'over-60': '>60%',
  'over-80': '>80%',
  'over-95': '>95%',
  full: '100%'
};

const FALLBACK_COLOUR = '#CCCCCC';

export interface DeliveryContainerCapacityRunPoint {
  x: [number, number];
  y: string;
  band: string;
  hoursInRun: number;
}

export interface DeliveryContainerCapacityModel {
  data: ChartData<'bar'>;
  groupNames: string[];
}

export function makeDeliveryContainerCapacityData(
  dto: DeliveryContainerUtilisationPeakBandsDto
): DeliveryContainerCapacityModel {
  const groupBy = dto.metadata.groupBy as KnownHistoricalUtilisationPeakBandsMetadataGroupBy;

  const orderedGroups = (() => {
    switch (groupBy) {
      case KnownHistoricalUtilisationPeakBandsMetadataGroupBy.Hub:
        return naturalSort(dto.groups).asc((el) => el.name);
      case KnownHistoricalUtilisationPeakBandsMetadataGroupBy.Size:
        return sortByLockerSize(dto.groups, (el) => el.name);
      default:
        return dto.groups;
    }
  })();

  const groupNames = orderedGroups.map((g) => g.name);

  // All runs collapse into a SINGLE dataset. Chart.js's multi-dataset bar grouping doesn't behave
  // predictably for floating bars (x: [start, end]) on a categorical index axis — later datasets
  // end up occluding earlier ones at the same row. With one dataset, each run is its own bar at
  // the full row height, coloured per-point from its band value. The band legend is generated
  // manually in the chart component.
  const points: DeliveryContainerCapacityRunPoint[] = orderedGroups.flatMap((g) =>
    g.runs.map((r) => ({
      x: [new Date(r.startTime).getTime(), new Date(r.endTime).getTime()] as [number, number],
      // `PeakBandRunBand` is an extensible enum, so the SDK types this as a bare string
      // rather than the CapacityBand union. Narrowed at lookup, falling back on anything
      // unrecognised.
      band: r.band,
      y: g.name,
      hoursInRun: r.hoursInRun
    }))
  );

  const colourForPoint = (ctx: ScriptableContext<'bar'>): string => {
    const raw = ctx.raw as DeliveryContainerCapacityRunPoint | undefined;
    if (!raw) {
      return FALLBACK_COLOUR;
    }
    return CAPACITY_BAND_COLOURS[raw.band as CapacityBand] ?? FALLBACK_COLOUR;
  };

  const dataset = {
    label: 'Capacity utilisation',
    data: points,
    backgroundColor: colourForPoint,
    borderColor: colourForPoint,
    borderWidth: 0,
    barPercentage: 0.95,
    categoryPercentage: 1
  } as unknown as ChartData<'bar'>['datasets'][number];

  return {
    data: { labels: groupNames, datasets: [dataset] },
    groupNames
  };
}
